import type {
  TableFilterQueryableFormValues,
  TableFilterQueryableSelectProps,
} from "./table-filter-queryable.type";
import type { ModelItem } from "@/modules/model";
import type {
  QueryableSelectOptionFormatProps,
  QueryableSelectProps,
} from "@/modules/queryable-select";
import type { Key } from "react";

import { FilterOutlined } from "@ant-design/icons";
import { Button, Col, Row, Select, Form } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useList } from "@/hooks/queries";
import QueryableSelect from "@/modules/queryable-select";
import { getObjectKeyValue } from "@/utilities/object";

// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;

const TableFilterQueryableSelect = ({
  model,
  withContext,
  filterBy = "name",
  primaryKey = "id",
  fields = [primaryKey, "name"],
  selectedKeys = [],
  setSelectedKeys,
  confirm,
  clearFilters,
  tableName,
}: TableFilterQueryableSelectProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<TableFilterQueryableFormValues>();

  const snakeCaseModel = model.replace(/([A-Z])/g, "_$1").toLowerCase();
  const fieldsParam =
    fields && fields.length > 0
      ? {
          [`fields[${snakeCaseModel}]`]: fields.join(","),
        }
      : null;
  const { isLoading, data } = useList<ModelItem>(model, {
    params: {
      [`page[size]`]: 50,
      [`filter[${primaryKey}]`]: selectedKeys.join(","),
      ...fieldsParam,
    },
    enabled: selectedKeys.length > 0,
  });
  const filteredModels = data?.data ?? [];
  const initialValues = filteredModels.map((model) => ({
    value: getObjectKeyValue(model, primaryKey),
    label: model.name,
  }));

  useEffect(() => {
    form.setFieldValue("queryableSelectFilter", initialValues);
  }, [initialValues]);

  const optionFormat = ({
    option,
  }: QueryableSelectOptionFormatProps<ModelItem>) => {
    const value = getObjectKeyValue(option, primaryKey);
    return (
      <Option key={value} value={value}>
        {option.name}
      </Option>
    );
  };

  const handleChange: QueryableSelectProps["onOptionSelected"] = (options) => {
    // `QueryableSelect` has `mode="multiple"` so `options` will always be
    // an Array. Let's just Array check to avoid TypeScript error.
    if (!Array.isArray(options)) return;

    if (options.length === 0) {
      clearFilters?.();
      confirm();
      return;
    }
  };

  const handleSubmit = (data: TableFilterQueryableFormValues) => {
    const keys = data.queryableSelectFilter?.map((opt) => opt.value);
    setSelectedKeys(keys as Key[]);
    confirm();
  };

  return (
    <div style={{ width: "300px" }}>
      <Form form={form} onFinish={handleSubmit}>
        <Row align="middle">
          <Col flex="auto">
            <QueryableSelect
              model={model}
              withContext={withContext}
              name="queryableSelectFilter"
              mode="multiple"
              tableName={tableName}
              filterBy={filterBy}
              fields={fields}
              optionFormat={optionFormat}
              parentLoading={isLoading}
              initialValue={initialValues}
              onOptionSelected={handleChange}
              selectStyle={{ width: "230px" }}
              formItemStyle={{ marginBottom: 0 }}
            />
          </Col>
          <Col flex="70px">
            <Button
              type="primary"
              icon={<FilterOutlined />}
              size="small"
              htmlType="submit"
              style={{ width: "70px", height: "32px" }}
            >
              {t("global:table:filter")}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default TableFilterQueryableSelect;
